'use client';

/**
 * Zustand store for the multi-step gift-sending form.
 *
 * Mirrors `proposal-form.ts` (the launchpad creation flow) but **without**
 * persist middleware — gifting is a one-shot interactive flow tied to the
 * user's current wallet balance, so persisting across sessions would confuse
 * the `maxOverrideD6` precision fix and the live handle/quote lookups.
 *
 * State resets when the orchestrator's `close()` runs.
 */

import type { StoreApi, UseBoundStore } from 'zustand';
import type { OutcomeHoldingChoice } from '@/components/gift/outcome-token-picker';
import { createTrackedStore } from '@/lib/dev/zustand-registry';
import type { Wallet } from '@/types/auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type GiftPlatform = 'morfi' | 'x';
export type GiftAsset = 'usdc' | 'outcome_token';

interface StepValidationError {
  key: string;
  params?: Record<string, string | number>;
}

type StepErrors = Record<string, StepValidationError>;

interface ResolveGiftSenderWalletInput {
  readonly wallets: readonly Wallet[];
  readonly senderWalletId: string;
  readonly activeWallet: Wallet | null | undefined;
  readonly multiWalletEnabled: boolean;
  readonly primaryWallet: Wallet | null;
}

export function resolveGiftSenderWallet({
  wallets,
  senderWalletId,
  activeWallet,
  multiWalletEnabled,
  primaryWallet,
}: ResolveGiftSenderWalletInput): Wallet | undefined {
  if (!multiWalletEnabled) {
    if (
      primaryWallet === null ||
      primaryWallet.archivedAt !== null ||
      primaryWallet.isAvailable === false
    ) {
      return undefined;
    }
    return primaryWallet;
  }

  const availableWallets = wallets.filter((wallet) => wallet.isAvailable !== false);
  return (
    availableWallets.find((wallet) => wallet.id === senderWalletId) ??
    availableWallets.find((wallet) => wallet.id === activeWallet?.id) ??
    availableWallets[0]
  );
}

interface GiftFormState {
  /**
   * Current form step (1–4):
   *   1 — Recipient (platform + handle)
   *   2 — Asset + amount (wallet source, USDC vs outcome token, amount)
   *   3 — Add a message (optional — message + anonymity; skippable)
   *   4 — Review and send
   */
  step: 1 | 2 | 3 | 4;

  // ── Step 1: Recipient ──────────────────────────────────────────────────
  platform: GiftPlatform;
  handle: string;

  // ── Step 2: Asset + amount ─────────────────────────────────────────────
  /** Wallet ID the sender is funding the gift from. Empty until first init. */
  senderWalletId: string;
  asset: GiftAsset;
  /**
   * The user's typed amount in DISPLAY currency (e.g. "25" for £25, "78.05"
   * for $78.05). NOT a USDC amount. Consumers MUST convert to USDC at the
   * boundary via `useFormattedCurrency().toUsdc(parseFloat(amount))` before
   * producing on-chain D6 base units.
   *
   * For USDC gifts: USD/local value of the cash to send.
   * For outcome-token gifts: USD/local value of the tokens to send —
   * converted to a token count at submit time using the holding's spot price.
   *
   * The Max button bypasses the local→USDC round-trip by pinning the exact
   * USDC base units to `maxOverrideD6Str`; submit then uses that bigint
   * directly instead of re-parsing this field.
   */
  amount: string;
  selectedHolding: OutcomeHoldingChoice | null;

  // ── Step 3: Message (optional) ─────────────────────────────────────────
  message: string;
  anonymous: boolean;
  /**
   * When set, submit uses this EXACT raw u64 D6 amount instead of parsing
   * the display string. Pinned by the Max button to bypass float
   * round-trip precision drift (was the cause of SPL Token InsufficientFunds).
   * Stored as a base-10 string so the store stays plain-data.
   */
  maxOverrideD6Str: string | null;

  // ── Tracking ───────────────────────────────────────────────────────────
  /** Steps the user has tried to advance past — gates progressive error display. */
  stepsAttempted: Set<number>;

  // ── Actions ────────────────────────────────────────────────────────────
  setStep: (step: 1 | 2 | 3 | 4) => void;
  markStepAttempted: (step: number) => void;
  setPlatform: (platform: GiftPlatform) => void;
  setHandle: (handle: string) => void;
  setSenderWalletId: (id: string) => void;
  setAsset: (asset: GiftAsset) => void;
  setAmount: (amount: string) => void;
  setSelectedHolding: (holding: OutcomeHoldingChoice | null) => void;
  setMessage: (message: string) => void;
  setAnonymous: (anonymous: boolean) => void;
  setMaxOverrideD6Str: (value: string | null) => void;
  reset: () => void;
}

const INITIAL_STATE: Omit<GiftFormState, keyof GiftFormActions> = {
  step: 1,
  platform: 'morfi',
  handle: '',
  senderWalletId: '',
  asset: 'usdc',
  amount: '',
  selectedHolding: null,
  message: '',
  anonymous: false,
  maxOverrideD6Str: null,
  stepsAttempted: new Set<number>(),
};

type GiftFormActions = Pick<
  GiftFormState,
  | 'setStep'
  | 'markStepAttempted'
  | 'setPlatform'
  | 'setHandle'
  | 'setSenderWalletId'
  | 'setAsset'
  | 'setAmount'
  | 'setSelectedHolding'
  | 'setMessage'
  | 'setAnonymous'
  | 'setMaxOverrideD6Str'
  | 'reset'
>;

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/** Step 1 validation — recipient is identified and looks valid up-front. */
export function getStep1Errors(state: GiftFormState): StepErrors {
  const errors: StepErrors = {};
  if (state.handle.trim().length === 0) {
    errors.handle = { key: 'handleRequired' };
  }
  return errors;
}

/**
 * Step 2 validation — there is a non-zero amount, asset is consistent.
 * Cross-validation against the live wallet balance happens in the orchestrator
 * (it owns the balance hook); here we only validate self-contained form state.
 */
export function getStep2Errors(state: GiftFormState): StepErrors {
  const errors: StepErrors = {};
  const amountNum = Number.parseFloat(state.amount);
  if (!Number.isFinite(amountNum) || amountNum <= 0) {
    errors.amount = { key: 'amountRequired' };
  }
  if (state.asset === 'outcome_token' && state.selectedHolding === null) {
    errors.holding = { key: 'holdingRequired' };
  }
  return errors;
}

const STEP_ATTEMPTED_SELECTORS: Record<number, (state: GiftFormState) => boolean> = {
  1: (state: GiftFormState): boolean => state.stepsAttempted.has(1),
  2: (state: GiftFormState): boolean => state.stepsAttempted.has(2),
  3: (state: GiftFormState): boolean => state.stepsAttempted.has(3),
  4: (state: GiftFormState): boolean => state.stepsAttempted.has(4),
};

export function selectStepAttempted(step: number): (state: GiftFormState) => boolean {
  const selector = STEP_ATTEMPTED_SELECTORS[step];
  if (selector !== undefined) return selector;
  return (state: GiftFormState): boolean => state.stepsAttempted.has(step);
}

export type { GiftFormState, StepErrors, StepValidationError };

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useGiftFormStore: UseBoundStore<StoreApi<GiftFormState>> =
  createTrackedStore<GiftFormState>('useGiftFormStore')((set) => ({
    ...INITIAL_STATE,

    setStep: (step: 1 | 2 | 3 | 4): void => {
      set({ step });
    },

    markStepAttempted: (step: number): void => {
      set((state) => {
        const next = new Set(state.stepsAttempted);
        next.add(step);
        return { stepsAttempted: next };
      });
    },

    setPlatform: (platform: GiftPlatform): void => {
      // Keep the handle text — switching platforms simply re-runs the
      // appropriate lookup (Morfi DB vs X API) against the same string.
      set({ platform });
    },

    setHandle: (handle: string): void => {
      set({ handle });
    },

    setSenderWalletId: (id: string): void => {
      // Switching the source wallet invalidates the Max override (it's pinned
      // to the previous wallet's exact balance) and the outcome-token holding
      // (holdings are per-wallet).
      set({ senderWalletId: id, maxOverrideD6Str: null, selectedHolding: null, amount: '' });
    },

    setAsset: (asset: GiftAsset): void => {
      // Switching asset clears amount + holding + Max override (cross-asset
      // values would never be valid in the other path).
      set({ asset, amount: '', selectedHolding: null, maxOverrideD6Str: null });
    },

    setAmount: (amount: string): void => {
      set({ amount });
    },

    setSelectedHolding: (holding: OutcomeHoldingChoice | null): void => {
      set({ selectedHolding: holding, amount: '', maxOverrideD6Str: null });
    },

    setMessage: (message: string): void => {
      set({ message });
    },

    setAnonymous: (anonymous: boolean): void => {
      set({ anonymous });
    },

    setMaxOverrideD6Str: (value: string | null): void => {
      set({ maxOverrideD6Str: value });
    },

    reset: (): void => {
      set({
        ...INITIAL_STATE,
        stepsAttempted: new Set<number>(),
      });
    },
  }));
